import { Injectable } from "@angular/core";
import { InputManager } from "./input-manager";
import { Transform, Vec3, vec3_up, vec3_forward, Mat4, Quaternion } from "./transform";
var MainCamera = (function () {
    function MainCamera(input_manager_) {
        this.input_manager_ = input_manager_;
        this.min_distance_target = 3.0;
        this.max_distance_target = 15.0;
        this.zoom_speed = 1.5;
        this.camera_orbit_velocity = 100;
        this.view_ = new Mat4();
        this.projection_ = new Mat4();
        this.transform_matrix_ = new Mat4();
        this.inverse_projection_ = new Mat4();
        this.lerp_duration = 0.4;
        this.current_lerp_time = 0;
        this.target_position_ = new Vec3(0.0, 1.0, 0.0);
    }
    Object.defineProperty(MainCamera.prototype, "view", {
        get: function () { return this.view_; },
        enumerable: true,
        configurable: true
    });
    ;
    Object.defineProperty(MainCamera.prototype, "projection", {
        get: function () { return this.projection_; },
        enumerable: true,
        configurable: true
    });
    ;
    Object.defineProperty(MainCamera.prototype, "inverse_view", {
        get: function () { return this.transform_matrix_; },
        enumerable: true,
        configurable: true
    });
    ;
    Object.defineProperty(MainCamera.prototype, "inverse_projection", {
        get: function () { return this.inverse_projection_; },
        enumerable: true,
        configurable: true
    });
    ;
    Object.defineProperty(MainCamera.prototype, "target_position", {
        set: function (position) {
            this.target_position_ = position;
        },
        enumerable: true,
        configurable: true
    });
    ;
    ;
    MainCamera.prototype.initialiseCamera = function (camera_data) {
        this.zfar = camera_data.zfar || 100;
        this.znear = camera_data.znear;
        this.yfov = camera_data.yfov;
        var initial_camera_offset = new Vec3(0.0, 1.0, 8.0);
        this.start_distance = this.end_distance = initial_camera_offset.length;
        this.updateOrthoNormalVectors(initial_camera_offset.normalise());
        var initial_camera_position = this.target_position_.add(initial_camera_offset).from_array;
        var initial_angle = Math.acos(initial_camera_offset.normalise().scale(-1.0).dot(vec3_forward));
        var initial_rotation = Quaternion.fromAxisAngle(new Vec3(-1.0, 0.0, 0.0), initial_angle).array;
        this.transform_ = new Transform(initial_camera_position, initial_rotation);
    };
    ;
    MainCamera.prototype.updateOrthoNormalVectors = function (view_normal) {
        this.view_forward = view_normal;
        this.view_right = this.view_forward.cross(vec3_up);
        this.view_right = this.view_right.normalise();
        this.view_up = this.view_right.cross(this.view_forward);
        this.view_up = this.view_up.normalise();
    };
    ;
    MainCamera.prototype.setTargetTransform = function (target_transform) {
    };
    ;
    MainCamera.prototype.updateCamera = function (dt) {
        var from_target = this.transform_.position.subtract(this.target_position_);
        this.updateOrthoNormalVectors(from_target.normalise());
        var updated_distance = this.updateCameraDistanceFromTarget(dt, from_target);
        var updated_rotation = this.updateCameraOrbitPosition(dt);
        var updated_direction = this.transform_.rotateAround(this.target_position_, updated_rotation);
        var rotated_point = updated_direction.scale(updated_distance);
        var updated_position = this.target_position_.add(rotated_point);
        this.transform_.setTranslation(updated_position);
        var look_at_rotation = this.transform_.lookAt(this.target_position_);
        this.transform_.setOrientation(look_at_rotation);
        this.transform_.updateTransform();
        this.transform_matrix_ = this.transform_.transform;
        this.view_ = this.transform_.inverse;
        this.setProjection();
        this.setInverseProjection();
    };
    ;
    MainCamera.prototype.updateCameraDistanceFromTarget = function (dt, from_target) {
        var wheel_direction = this.input_manager_.wheel;
        var current_distance = from_target.length;
        if (wheel_direction !== 0) {
            this.current_lerp_time = 0;
            this.start_distance = current_distance;
            var zoom = wheel_direction * this.zoom_speed;
            var desired_distance = current_distance + zoom;
            this.end_distance = (desired_distance <= this.min_distance_target) ?
                this.min_distance_target : (desired_distance >= this.max_distance_target) ?
                this.max_distance_target : desired_distance;
        }
        this.current_lerp_time += dt;
        if (this.current_lerp_time > this.lerp_duration) {
            this.current_lerp_time = this.lerp_duration;
        }
        var t = this.current_lerp_time / this.lerp_duration;
        return (1 - t) * this.start_distance + (t * this.end_distance);
    };
    ;
    MainCamera.prototype.updateCameraOrbitPosition = function (dt) {
        if (!this.input_manager_.isButtonDown("right"))
            return new Quaternion();
        var dx = this.input_manager_.delta.x;
        var dy = this.input_manager_.delta.y;
        var rotation = new Quaternion();
        if (dx) {
            var rotation_angle = dx * this.camera_orbit_velocity * dt;
            var x_rotation = Quaternion.fromAxisAngle(vec3_up, rotation_angle);
            rotation = x_rotation.multiply(rotation);
        }
        if (dy) {
            var rotation_angle = dy * this.camera_orbit_velocity * dt;
            var y_rotation = Quaternion.fromAxisAngle(this.view_right, rotation_angle);
            rotation = y_rotation.multiply(rotation);
        }
        return rotation;
    };
    ;
    MainCamera.prototype.getInputRotation = function (input_x, input_y) {
        var input_rotation;
        var scaled_up = this.view_up.scale(input_y);
        var scaled_right = this.view_right.scale(input_x);
        var scaled_xy = scaled_right.add(scaled_up);
        var length_squared = scaled_xy.squared_length;
        if (length_squared < 1.0) {
            var z_length = Math.sqrt(1.0 - length_squared);
            var scaled_view = this.view_forward.scale(z_length);
            input_rotation = scaled_xy.add(scaled_view);
        }
        else {
            input_rotation = scaled_xy.normalise();
        }
        return input_rotation;
    };
    ;
    MainCamera.prototype.setProjection = function () {
        var aspect = this.input_manager_.aspect;
        var f = Math.tan(0.5 * (Math.PI - this.yfov));
        var depth = 1.0 / (this.znear - this.zfar);
        this.projection_.array[0] = f / aspect;
        this.projection_.array[5] = f;
        this.projection_.array[10] = (this.znear + this.zfar) * depth;
        this.projection_.array[11] = -1.0;
        this.projection_.array[14] = 2.0 * (this.znear * this.zfar) * depth;
    };
    ;
    MainCamera.prototype.setInverseProjection = function () {
        this.inverse_projection_.array[0] = 1.0 / this.projection_.array[0];
        this.inverse_projection_.array[5] = 1.0 / this.projection_.array[5];
        this.inverse_projection_.array[11] = 1.0 / this.projection_.array[14];
        this.inverse_projection_.array[14] = -1.0;
        this.inverse_projection_.array[15] = this.projection_.array[10] / this.projection_.array[14];
    };
    ;
    MainCamera.decorators = [
        { type: Injectable },
    ];
    MainCamera.ctorParameters = function () { return [
        { type: InputManager, },
    ]; };
    return MainCamera;
}());
export { MainCamera };
//# sourceMappingURL=main-camera.js.map